import { ApiNote } from './types';
import { GpsPoint } from '../types';
import { message } from 'antd';

/**
 * Get distance between two points in KM, from their GPS coords
 *
 *  @param {Object} { firstPoint, secondPoint }
 *  @return {number} - The distance (in KM)
 */
export const getDistanceInKmFromGpsCoord = ({
  firstPoint,
  secondPoint,
}: {
  firstPoint: GpsPoint;
  secondPoint: GpsPoint;
}): number => {
  // Radius of the earth in km
  const EARTH_RADIUS = 6371;

  const latitudeDiff = degToRad(secondPoint?.lat - firstPoint?.lat);
  const longitudeDiff = degToRad(secondPoint?.lng - firstPoint?.lng);

  const a =
    Math.sin(latitudeDiff / 2) * Math.sin(latitudeDiff / 2) +
    Math.cos(degToRad(firstPoint?.lat)) *
      Math.cos(degToRad(secondPoint?.lat)) *
      Math.sin(longitudeDiff / 2) *
      Math.sin(longitudeDiff / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = EARTH_RADIUS * c;
  return distance;
};

/**
 * Converts ° to rads
 *
 *  @param {number} - The ° value
 *  @return {number} - The rad value
 */
export const degToRad = (deg: number) => {
  return deg * (Math.PI / 180);
};

/**
 * Sort an Array of object according to their distance from a GPS coords, in ascending order
 *
 *  @param {Object} { array: An Array of Objects with a location property, Location: the GPS coordinates }
 *  @return {Array} - An Array ordered by distance from the location, in ascending order
 */
export const sortArrayPerDistanceFromGpsLocation = ({
  array,
  location,
}: {
  array: Array<any>;
  location: GpsPoint;
}) => {
  return array.sort((firstItem, secondItem) => {
    if (!firstItem)
      firstItem = {
        location: {
          lng: 0,
          lat: 0,
        },
      };
    if (!secondItem)
      secondItem = {
        location: {
          lng: 0,
          lat: 0,
        },
      };
    return (
      getDistanceInKmFromGpsCoord({
        firstPoint: location,
        secondPoint: firstItem.location,
      }) -
      getDistanceInKmFromGpsCoord({
        firstPoint: location,
        secondPoint: secondItem.location,
      })
    );
  });
};

/**
 *  Builds a timeline from the internal notes, for demands and volunteers details
 *
 * @param {Object} - { commentsHistory: Array of internal notes }
 * @return {Array} - An Array with the required data for the timeline view, ordered from newest to oldest
 */
export const buildHistory = ({
  commentsHistory = [],
}: {
  commentsHistory: Array<ApiNote>;
}) => {
  return commentsHistory
    .map(
      ({
        id,
        publisher: { id: publisher_id, name, avatar },
        created_at,
        text,
      }: any) => {
        return {
          id,
          publisher_id,
          name: name,
          avatar: avatar?.url.original,
          text: text,
          created_at: created_at,
        };
      }
    )
    .sort(
      ({ created_at: dateOne }, { created_at: dateTwo }) =>
        new Date(dateTwo).getTime() - new Date(dateOne).getTime()
    );
};

/**
 * Generate a random id for data that doesn't have one
 * @param {string} given string from the data
 * @return {string} generate id with the given string
 */
export const generateId = (value: string) =>
  `${value} - ${Math.floor(1 + Math.random()) * 0x100}`;

export const getStatusColors = (position: number) => {
  if (position === 1) return '#F25B5B';
  if (position > 1 && position < 99) return '#4FA6D4';
  if (position === 99) return '#24ACAC';

  return '#FFFFFF';
};

/**
 * Compare two objects
 * @param {any} firstObject first object
 * @param {any} secondObject second object
 * @return {boolean} true if the two objects are equal or false
 */
export const comparerObjects = (firstObject: any, secondObject: any) => {
  if (!firstObject || !secondObject) {
    return false;
  }

  for (const k of Object.keys(firstObject)) {
    if (typeof firstObject[k] !== 'object') {
      if (
        typeof firstObject[k] === 'function' &&
        typeof secondObject[k] === 'function'
      ) {
        return firstObject[k].toString() === secondObject[k].toString();
      } else if (firstObject[k] !== secondObject[k]) return false;
    } else {
      comparerObjects(firstObject[k], secondObject[k]);
    }
  }
  return true;
};

export const errorController = (
  errors: { message: string }[],
  text: string
) => {
  let phoneError = null;
  let emailError = null;

  errors.forEach((error: { message: string }) => {
    if (error.message.includes('phone_associated')) {
      phoneError = `Ce numéro de téléphone est déjà utilisé par un ${text}.`;
    }
    if (error.message.includes('email_associated')) {
      emailError = `Cette adresse email est déjà utilisée par un ${text}.`;
    }
  });

  if (emailError && phoneError)
    message.error(
      `Cette adresse email et ce numéro de téléphone sont déjà utilisés par un ${text}.`
    );
  else {
    emailError && message.error(emailError);
    phoneError && message.error(phoneError);
  }
};

export const capitalizeFirstLetter = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);
